
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StarIcon } from "lucide-react";
import { toast } from "sonner";
import analytics from '@/utils/analytics';
import { supabase } from "@/integrations/supabase/client";
import { useUser } from '@/context/UserContext';

interface ProgramCheckInProps {
  day: number;
  onComplete: () => void;
}

const ProgramCheckIn: React.FC<ProgramCheckInProps> = ({ day, onComplete }) => {
  const [rating, setRating] = useState<number>(0);
  const [feedback, setFeedback] = useState<string>('');
  const [improvements, setImprovements] = useState<string>('');
  const [selected, setSelected] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useUser();
  
  const isDay7 = day === 7;
  const isDay30 = day === 30;
  
  const multipleChoiceOptions = isDay7 
    ? [
        "I feel more energetic",
        "I'm sleeping better",
        "I notice less mood swings", 
        "I haven't noticed much difference yet",
        "I'm struggling to keep up with the program"
      ]
    : [
        "I've seen significant improvements",
        "I feel more in tune with my body",
        "I have more energy and less symptoms",
        "I've learned a lot but haven't seen physical changes",
        "I didn't complete all parts of the program"
      ];
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      // In a real app, this would send data to a backend
      analytics.trackUserAction('program_checkin_completed', {
        day,
        rating,
        selectedOption: selected,
        feedback: feedback.substring(0, 50) + (feedback.length > 50 ? '...' : ''),
        improvementsFeedback: improvements.substring(0, 50) + (improvements.length > 50 ? '...' : '')
      });
      
      // If user is logged in, save badge to user_badges table
      if (user?.id) {
        const badgeData = {
          user_id: user.id,
          badge_id: isDay7 ? 'week-1-completion' : 'program-completion',
          badge_name: isDay7 ? 'Week 1 Milestone' : 'Program Completion',
          badge_type: isDay7 ? 'bronze' : 'gold',
          badge_description: isDay7 
            ? 'Completed your first week of the program!'
            : 'Successfully completed the entire 30-day program!',
          associated_lesson: day
        };
        
        await supabase.from('user_badges').insert(badgeData);
      }
      
      toast.success(`Day ${day} program check-in completed!`);
      
      // Call the onComplete callback
      onComplete();
    } catch (error) {
      console.error('Error submitting program check-in:', error);
      toast.error('Failed to submit your feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="bg-white p-6 rounded-2xl border border-hormony-beige/50 shadow-sm animate-slide-up max-w-2xl mx-auto">
      <h2 className="text-xl font-semibold text-hormony-charcoal mb-1">
        {isDay7 ? 'Week 1 Check-in' : 'Program Completion Check-in'}
      </h2>
      <p className="text-hormony-charcoal/70 mb-6">
        {isDay7 
          ? 'Congratulations on your first week! Please share your experience so far.'
          : 'Congratulations on completing the program! Please share your overall experience.'}
      </p>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Rating */}
        <div className="space-y-2">
          <label className="block text-hormony-charcoal/80 text-sm font-medium mb-1">
            How would you rate your experience so far?
          </label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                className={`transition-all ${
                  star <= rating ? 'text-yellow-400 scale-110' : 'text-gray-300 hover:text-gray-400'
                }`}
              >
                <StarIcon className="w-8 h-8 fill-current" />
              </button>
            ))}
          </div>
        </div>
        
        {/* Multiple Choice */}
        <div className="space-y-2">
          <label className="block text-hormony-charcoal/80 text-sm font-medium mb-1">
            Which best describes your experience?
          </label>
          <div className="space-y-2">
            {multipleChoiceOptions.map((option) => (
              <label
                key={option}
                className={`block p-3 rounded-lg border transition-all cursor-pointer hover:bg-hormony-beige/10 ${
                  selected === option
                    ? 'border-hormony-coral bg-hormony-beige/20'
                    : 'border-hormony-beige/50'
                }`}
              >
                <div className="flex items-center">
                  <input
                    type="radio"
                    name="experience"
                    value={option}
                    checked={selected === option}
                    onChange={() => setSelected(option)}
                    className="sr-only"
                  />
                  <div className={`h-5 w-5 rounded-full border flex items-center justify-center mr-3 ${
                    selected === option
                      ? 'border-hormony-coral bg-hormony-coral'
                      : 'border-hormony-charcoal/30'
                  }`}>
                    {selected === option && (
                      <div className="h-2 w-2 rounded-full bg-white"></div>
                    )}
                  </div>
                  <span>{option}</span>
                </div>
              </label>
            ))}
          </div>
        </div>
        
        {/* Free text - Experience */}
        <div className="space-y-2">
          <label className="block text-hormony-charcoal/80 text-sm font-medium">
            {isDay7 
              ? 'How has this program helped you so far?'
              : 'How has this program impacted your hormone health?'}
          </label>
          <Textarea
            placeholder="Share your thoughts..."
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            className="min-h-[100px]"
          />
        </div>
        
        {/* Free text - Improvements */}
        <div className="space-y-2">
          <label className="block text-hormony-charcoal/80 text-sm font-medium">
            What improvements would you like to see in the program?
          </label>
          <Textarea
            placeholder="Any suggestions for us..."
            value={improvements}
            onChange={(e) => setImprovements(e.target.value)}
            className="min-h-[100px]"
          />
        </div>
        
        <Button
          type="submit"
          className="w-full"
          disabled={!rating || !selected || isSubmitting}
        >
          {isSubmitting 
            ? 'Submitting...' 
            : isDay7 ? 'Submit & Continue to Week 2' : 'Complete Program Check-in'}
        </Button>
      </form>
    </div>
  );
};

export default ProgramCheckIn;
